import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FireAnimation from './pages/fireanimation';

const menuItems = ['TOOLS', 'ABOUT'];

const MainMenu = ({ visible, theme }) => {
  const [dateTime, setDateTime] = useState(new Date());
  const [isConnected, setIsConnected] = useState(true);
  const [notificationExpanded, setNotificationExpanded] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const [newsData, setNewsData] = useState(null);

  useEffect(() => {
    const fetchLiveStats = async () => {
      try {
        const response = await fetch('https://api.spookytools.com/api/status');
        const data = await response.json();
        if (data?.news) {
          setNewsData(data.news);
        }
      } catch (error) {
        console.error('Failed to fetch live stats:', error);
        setIsConnected(false);
      }
    };

    fetchLiveStats();
    const intervalId = setInterval(fetchLiveStats, 5000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden">
      {/* Backdrop filter for background video blur */}
      <div className="absolute inset-0 backdrop-blur-sm z-0"></div>
      
      {/* Main content container */}
      <div
        className={`relative h-full w-full flex flex-col items-stretch justify-center p-4 sm:p-6 md:p-8 transition-opacity duration-500 z-20 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Top Bar with Status */}
        <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-2 z-30">
          <div 
            className="flex items-center space-x-2 bg-black bg-opacity-70 border-l-2 border-orange-600 px-3 py-2 cursor-pointer"
            onClick={() => setNotificationExpanded(!notificationExpanded)}
          >
            <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-sm text-white font-bold uppercase">System Status</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-5 w-5 text-white transition-transform duration-300 ${notificationExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          <div className="text-gray-400 text-sm bg-black bg-opacity-70 px-2 py-1">
            {dateTime.toLocaleString()}
          </div>
        </div>
        
        {/* Enhanced Notification Panel */}
        <div 
          className={`absolute top-12 left-0 bg-black bg-opacity-90 border-l-2 border-orange-600 z-40 transition-all duration-300 overflow-hidden shadow-lg ${
            notificationExpanded ? 'w-72 sm:w-80 md:w-96 max-h-96' : 'max-h-0 w-0'
          }`}
        >
          <div className="p-4 text-white">
            <div className="font-bold text-lg mb-3 text-orange-500 border-b border-gray-700 pb-2">
              {newsData?.title || "SYSTEM NEWS & NOTIFICATIONS"}
            </div>
            <div className="space-y-4">
              {newsData ? (
                <div className="bg-gray-900 bg-opacity-60 p-3 rounded">
                  <div className="font-bold text-sm mb-1 text-orange-400">{newsData.description}</div>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    {newsData.items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <div className="text-gray-400 text-xs mt-2">
                    Last updated: {new Date(newsData.date).toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 bg-opacity-60 p-3 rounded">
                  <div className="font-bold text-sm mb-1 text-orange-400">KEYBOARD SHORTCUTS</div>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Ctrl + M to Mute background Audio</li>
                    <li>Esc to close current panel</li>
                    <li>F1 for help menu</li>
                  </ul>
                  <div className="text-gray-400 text-xs mt-2">Loading updates...</div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Center Container - Logo and Menu */}
        <div className="flex flex-col items-center justify-center space-y-6">
          {/* Logo - BO3 Style */}
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg relative">
            <div className="absolute -left-2 top-1/2 h-px w-6 bg-orange-600"></div>
            <div className="absolute -left-4 top-1/2 h-3 w-3 bg-white"></div>
            <img
              src={theme.images.logo}
              alt="Logo"
              className="h-auto w-full"
            />
            <div className="absolute -right-2 top-1/2 h-px w-6 bg-orange-600"></div>
          </div>
          
          {/* Navigation - BO3 Style */}
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
            <div className="w-full h-px bg-orange-600 mb-2 opacity-50"></div>
            {menuItems.map((item, index) => (
              <div key={item} className="mb-2 last:mb-0">
                <Link 
                  to={`/${item.toLowerCase().replace(/\s/g, '-')}`}
                  className="block relative group"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="flex items-center bg-black bg-opacity-50 border-b border-gray-800 group-hover:bg-orange-500 transition-all duration-200 py-2 px-4 group-hover:border-orange-300 group-hover:shadow-[0_0_15px_rgba(249,115,22,0.6)]">
                    <span className="text-white text-sm sm:text-base font-bold tracking-wider uppercase group-hover:text-black transition-all duration-200">{item}</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-4 w-4 text-orange-600 group-hover:text-black ml-auto opacity-0 group-hover:opacity-100 transition-all duration-200" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </div>
            ))}
            <div className="w-full h-px bg-orange-600 mt-2 opacity-50"></div>
          </div>
        </div>
        
        {/* Footer Status */}
        <div className="absolute bottom-2 left-2 text-gray-500 text-xs bg-black bg-opacity-70 p-1 rounded z-30">
          Connection Status: <span className={isConnected ? 'text-green-500' : 'text-red-500'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        
        {/* Character Display (Right Bottom) */}
        <div className="hidden md:block absolute bottom-0 right-0 z-30">
          <div className="relative w-32 lg:w-40 h-32 lg:h-40">
            {/* Stamp/shadow effect */}
            <div className="absolute -left-32 lg:-left-40 bottom-0 w-32 lg:w-40 h-32 lg:h-40 opacity-30">
              <img
                src="https://cdn.spookytools.com/assets/Stamp2.png"
                alt="Stamp2"
                className="w-full h-full object-contain"
                style={{ filter: 'invert(1)' }}
              />
            </div>
            
            {/* Skull with fire - Reordered for skull to be on top */}
            <div className="absolute -top-6 left-4 w-20 lg:w-24 h-20 lg:h-24 z-50">
              <div className="absolute left-0 w-20 lg:w-24 h-20 lg:h-24 z-10">
                <FireAnimation />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </div>
  );
};

const BO3Menu = ({ theme }) => {
  const [mainOpacity, setMainOpacity] = useState(false);

  useEffect(() => {
    // Show main menu immediately with animation
    setTimeout(() => setMainOpacity(true), 50);
  }, []);

  return <MainMenu visible={mainOpacity} theme={theme} />;
};

export default BO3Menu;
